import { supabase } from '../../../supabaseClient';

// ── Jugador del día (sistema Wordle) ──────────────────────────────────────────

export const obtenerJugadorOculto = async () => {
  const { data: jugadorId, error: rpcError } = await supabase
    .rpc('get_formidable_del_dia');

  if (rpcError || !jugadorId) return null;

  const { data: jugador, error } = await supabase
    .from('jugadores')
    .select(`
      id, nombre, apellido, nacionalidad, jugo_en_seleccion,
      formidable_year, formidable_year_alt, retirement_year, retired,
      apodo, tipo_apodo,
      foto_url, youtube_url, seleccion_emoji, club_formidable,
      posicion:posiciones(sigla, nombre),
      club:clubes(sigla)
    `)
    .eq('id', jugadorId)
    .single();

  if (error || !jugador) return null;

  const { data: historial } = await supabase
    .from('player_history')
    .select(`
      year_from, year_to,
      team:teams(
        id, name,
        country:countries(name, continent:continents(name))
      )
    `)
    .eq('player_id', jugador.id)
    .order('year_from', { ascending: true });

  return { jugador, historial: historial || [] };
};

// ── Catálogos ─────────────────────────────────────────────────────────────────

export const obtenerClubes = async () => {
  const { data } = await supabase.from('clubes').select('id, sigla, nombre').order('nombre');
  return data || [];
};

export const obtenerEquipos = async () => {
  const { data } = await supabase
    .from('teams')
    .select('id, name, country:countries(name, continent:continents(name))')
    .order('name', { ascending: true });
  return data || [];
};

export const obtenerJugadoresParaAutocompletar = async () => {
  const { data } = await supabase
    .from('jugadores')
    .select('id, nombre, apellido, posicion:posiciones(sigla)')
    .not('formidable_year', 'is', null)
    .eq('contexto', 'formidable')
    .order('apellido', { ascending: true });
  return data || [];
};

// ── Evaluadores ───────────────────────────────────────────────────────────────

export const preguntaJugoEnClub = (historial, teamId) =>
  historial.some(h => h.team?.id === teamId);

export const preguntaRetirado = (jugador) => jugador.retired === true;

export const preguntaAnioRetiro = (jugador, anio) => {
  if (!jugador.retired || !jugador.retirement_year) return 'no_retirado';
  if (anio === jugador.retirement_year) return 'exacto';
  if (anio < jugador.retirement_year)  return 'después';
  return 'antes';
};

export const preguntaAnioFormidable = (jugador, anio) => {
  if (anio === jugador.formidable_year) return 'exacto';
  if (jugador.formidable_year_alt && anio === jugador.formidable_year_alt) return 'exacto';
  if (anio < jugador.formidable_year)  return 'después';
  return 'antes';
};

export const preguntaJugoEnRegion = (historial, region) => {
  const norm = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  const r = norm(region);

  // Mapeo de alias → palabras clave para matchear país o continente
  const ALIAS = {
    'europa':       ['europa','europe','españa','italia','alemania','francia','inglaterra','portugal','holanda','belgica','turquia','ucrania','escocia','grecia','rumania','rumania'],
    'sudamerica':   ['sudamerica','south america','america del sur','brasil','colombia','chile','uruguay','paraguay','peru','bolivia','venezuela','ecuador'],
    'argentina':    ['argentina'],
    'brasil':       ['brasil','brazil'],
    'españa':       ['españa','spain'],
    'italia':       ['italia','italy'],
    'mexico':       ['mexico'],
    'ucrania':      ['ucrania','ukraine'],
    'inglaterra':   ['inglaterra','england'],
    'portugal':     ['portugal'],
    'kuwait':       ['kuwait'],
    'grecia':       ['grecia','greece'],
    'colombia':     ['colombia'],
    'paraguay':     ['paraguay'],
    'peru':         ['peru'],
    'uruguay':      ['uruguay'],
    'concacaf':     ['mexico','estados unidos','canada','panama','costa rica'],
    'asia':         ['japon','corea','china','emiratos','arabia','qatar','indonesia'],
  };

  // Buscar alias que matchee con la región ingresada
  let keywords = null;
  for (const [key, vals] of Object.entries(ALIAS)) {
    if (r.includes(key) || vals.some(v => r.includes(v))) {
      keywords = vals;
      break;
    }
  }
  // Si no encontró alias, usar la región directamente
  if (!keywords) keywords = [r];

  // Filtrar historial
  const periodos = historial.filter(h => {
    const pais = norm(h.team?.country?.name || '');
    const continente = norm(h.team?.country?.continent?.name || '');
    return keywords.some(k => pais.includes(k) || continente.includes(k));
  });

  return periodos;
};

export const preguntaPosicion = (jugador, sigla) =>
  (jugador.posicion?.sigla || '').toUpperCase() === sigla.toUpperCase();

export const verificarIntento = (jugador, intentoId) =>
  jugador.id === intentoId;

// ── Cooldown — por fecha calendario ──────────────────────────────────────────

export const checkCooldownFormidable = async (userId) => {
  const { data: perfil } = await supabase
    .from('perfiles')
    .select('formidable_ultimo_jugado')
    .eq('id', userId)
    .single();

  if (!perfil?.formidable_ultimo_jugado) return { puedeJugar: true, horasRestantes: 0 };

  const ultimoJugado = new Date(perfil.formidable_ultimo_jugado);
  const hoy = new Date();

  const mismodia =
    ultimoJugado.getFullYear() === hoy.getFullYear() &&
    ultimoJugado.getMonth()    === hoy.getMonth()    &&
    ultimoJugado.getDate()     === hoy.getDate();

  if (!mismodia) return { puedeJugar: true, horasRestantes: 0 };

  const manana = new Date(hoy);
  manana.setDate(manana.getDate() + 1);
  manana.setHours(0, 0, 0, 0);
  const horasRestantes = Math.ceil((manana - hoy) / (1000 * 60 * 60));

  return { puedeJugar: false, horasRestantes };
};

// ── Guardar resultado ─────────────────────────────────────────────────────────

export const guardarResultado = async (userId, jugador, cantPreguntas, seRindio = false) => {
  const { data: perfil } = await supabase
    .from('perfiles')
    .select('minijuegos_historial, formidable_total_victorias, racha_formidable_preguntas')
    .eq('id', userId)
    .single();

  const entrada = {
    juego: 'Formidable',
    jugador: `${jugador.nombre} ${jugador.apellido}`,
    preguntas: cantPreguntas,
    seRindio,
    fecha: new Date().toISOString(),
  };

  const totalActual = perfil?.formidable_total_victorias || 0;

  const updates = {
    minijuegos_historial: [...(perfil?.minijuegos_historial || []), entrada],
    formidable_ultimo_jugado: new Date().toISOString(),
  };

  if (!seRindio) {
    updates.formidable_total_victorias = totalActual + 1;
    const mejorPreguntas = perfil?.racha_formidable_preguntas || 0;
    if (mejorPreguntas === 0 || cantPreguntas < mejorPreguntas) {
      updates.racha_formidable_preguntas = cantPreguntas;
    }
  }
  // Si pierde: total nunca baja

  await supabase.from('perfiles').update(updates).eq('id', userId);

  return { entrada, totalVictorias: updates.formidable_total_victorias ?? totalActual };
};